import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import DOMPurify from 'dompurify';
import { isHtmlBody } from '../../lib/richText';

const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'b', 'em', 'i', 'blockquote', 'pre', 'code',
    'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
};

/**
 * Renders ticket/comment body as sanitized HTML or plain text.
 * @param {{
 *   body?: string,
 *   bodyFormat?: string,
 *   variant?: 'body1' | 'body2',
 *   sx?: object,
 * }} props
 */
export default function RichTextDisplay({ body, bodyFormat, variant = 'body2', sx }) {
  const html = useMemo(() => {
    if (!body) return '';
    if (!isHtmlBody(body, bodyFormat)) return '';
    return DOMPurify.sanitize(body, PURIFY_CONFIG);
  }, [body, bodyFormat]);

  if (!body) {
    return null;
  }

  if (!html) {
    return (
      <Typography variant={variant} sx={{ whiteSpace: 'pre-wrap', ...sx }}>
        {body}
      </Typography>
    );
  }

  return (
    <Box
      className="rich-text-display"
      component="div"
      sx={{
        typography: variant,
        '& p': { m: 0, mb: 0.75, '&:last-child': { mb: 0 } },
        '& blockquote': {
          borderLeft: 3,
          borderColor: 'divider',
          pl: 1.5,
          ml: 0,
          my: 0.75,
          color: 'text.secondary',
        },
        '& pre': {
          bgcolor: 'action.selected',
          borderRadius: 1,
          p: 1,
          overflow: 'auto',
          fontSize: '0.85rem',
          my: 0.75,
        },
        '& code': {
          fontFamily: 'monospace',
          fontSize: '0.85em',
          bgcolor: 'action.hover',
          px: 0.5,
          borderRadius: 0.5,
        },
        '& pre code': { bgcolor: 'transparent', p: 0 },
        '& a': { color: 'primary.main', wordBreak: 'break-all' },
        ...sx,
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
